import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Popover } from 'antd';
import dayjs from 'dayjs';
import {
  FiPlus,
  FiFilter,
  FiDownload,
  FiCalendar,
  FiFileText,
  FiCheckCircle,
  FiEdit2,
  FiEye,
  FiTrash2,
  FiMoreVertical,
  FiChevronRight,
} from 'react-icons/fi';
import { BsHourglassSplit } from 'react-icons/bs';

import firebase from '../../config/firebase.config';
import { getTeamMemberDetails } from '../../services/teamMemberDetails';
import { leaveBalanceService } from '@/services/leaveBalance';
import { appRoutes } from '@/routes';
import { resolveDepartmentIcon } from '@/utils/departmentIcons';
import { escapeCSVField, downloadCSV } from '@/utils/csvExport';
import {
  LEAVE_TYPE_FILTER_OPTIONS,
  LEAVE_TYPE_TONE,
  LEAVE_STATUS_FILTER_OPTIONS,
  LEAVE_STATUS_TONE,
  parseLeaveDateString,
} from '@/utils/leaveTaxonomy';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { StatCard } from '@/components/StatCard';
import { StatusBadge, statusBadgeTones } from '@/components/StatusBadge';
import { InitialsBadge } from '@/components/InitialsBadge';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { DateRangePicker } from '@/components/DateRangePicker';
import { Button, buttonVariants } from '@/components/Button';
import { Table } from '@/components/Table';
import { Pagination } from '@/components/Pagination';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner/Spinner.component';
import { ActionsMenu } from '@components/ActionsMenu';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { addNotification, notificationStates } from '@components/Notification';

import { LeaveFormModal } from './LeaveFormModal.component';
import { UpdateLeaveFormModal } from './UpdateLeaveFormModal.component';
import { ViewLeaveModal } from './ViewLeaveModal.component';
import styles from './Leave.module.scss';

const database = firebase.firestore();
const PAGE_SIZE = 10;
const MANAGER_ROLES = ['Manager', 'Admin'];

const STAT_CARDS_CONFIG = [
  {
    key: 'totalAllocatedLeaves',
    label: 'Total Allocated Leaves',
    icon: <FiCalendar />,
    iconBg: 'var(--purple-100)',
  },
  {
    key: 'usedLeaves',
    label: 'Used Leaves',
    icon: <FiFileText />,
    iconBg: 'var(--blue-100)',
  },
  {
    key: 'availableLeaves',
    label: 'Available Leaves',
    icon: <FiCheckCircle />,
    iconBg: 'var(--success-100)',
  },
  {
    key: 'pendingRequests',
    label: 'Pending Requests',
    icon: <BsHourglassSplit />,
    iconBg: 'var(--warning-100)',
  },
];

function PlainCell({ children }) {
  return (
    <Text variant={textVariantsEnum.Text_sm} fontWeight={fontWeightEnum.Normal}>
      {children}
    </Text>
  );
}

function DateCell({ date }) {
  if (!date) return <PlainCell>—</PlainCell>;
  return (
    <div className={styles.dateCell}>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {date.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </Text>
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Normal}
        color="gray-500"
      >
        ({date.toLocaleDateString(undefined, { weekday: 'short' })})
      </Text>
    </div>
  );
}

function ManagerCell({ manager }) {
  if (!manager) return <PlainCell>—</PlainCell>;
  return (
    <div className={styles.personCell}>
      <InitialsBadge label={manager.userName} twoLetter />
      <div className={styles.personCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {manager.userName}
        </Text>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          {manager.role || '—'}
        </Text>
      </div>
    </div>
  );
}

function DepartmentCell({ teamName }) {
  const { Icon, iconColor, iconBg } = resolveDepartmentIcon(teamName);
  return (
    <div className={styles.departmentCell}>
      <span
        className={styles.departmentIconWrap}
        style={{ backgroundColor: iconBg }}
      >
        <Icon size={14} color={iconColor} />
      </span>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {teamName || '—'}
      </Text>
    </div>
  );
}

export const LeaveComponent = () => {
  const history = useHistory();
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { profile } = useSelector((state) => state.auth);
  const rowDataRef = useRef(null);

  const [showAddLeaveFormModal, setShowAddLeaveFormModal] = useState(false);
  const [showUpdateLeaveModal, setShowUpdateLeaveModal] = useState(false);
  const [viewRequest, setViewRequest] = useState(null);
  const [activeMenuRow, setActiveMenuRow] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const menuRef = useRef(null);
  const [leaveRequestsList, setLeaveRequestsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leaveData, setLeaveData] = useState({
    loading: true,
    totalAllocatedLeaves: 0,
    usedLeaves: 0,
    availableLeaves: 0,
    pendingRequests: 0,
  });
  const [companyManagers, setCompanyManagers] = useState([]);
  const [teamNameMap, setTeamNameMap] = useState({});

  const [dateRange, setDateRange] = useState(() => [
    dayjs().startOf('month').toDate(),
    dayjs().endOf('month').toDate(),
  ]);
  const [typeFilter, setTypeFilter] = useState(LEAVE_TYPE_FILTER_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState(
    LEAVE_STATUS_FILTER_OPTIONS[0]
  );
  const [sortedBy, setSortedBy] = useState(null);
  const [reverseSort, setReverseSort] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Company leave: single source — companyDatabase/{companyId}/leaveRequests
  const fetchLeaveRequestsList = () => {
    if (!activeCompany?.companyId) return;
    const data = [];
    setLoading(true);
    database
      .collection('companyDatabase')
      .doc(activeCompany.companyId)
      .collection('leaveRequests')
      .where('userId', '==', profile.id)
      .get()
      .then((res) => {
        res.docs.forEach((docSnap) => {
          data.push({ ...docSnap.data(), id: docSnap.id });
        });
        setLeaveRequestsList(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  const fetchLeaveBalance = async () => {
    try {
      const balance = await leaveBalanceService.getEffectiveRemainingPaidLeave(
        profile.id,
        activeCompany?.companyId
      );
      setLeaveData({
        loading: false,
        totalAllocatedLeaves: balance.totalAllocated,
        usedLeaves: balance.used,
        availableLeaves: balance.effectiveRemaining,
        pendingRequests: balance.pendingRequests,
      });
    } catch (error) {
      console.log(error, '>>> Leave.component.jsx <<<', 'line-74');
      setLeaveData((prev) => ({ ...prev, loading: false }));
    }
  };
  useEffect(() => {
    fetchLeaveBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load company-wide managers + team names via getTeamMemberDetails
  // (supports legacy + sharded), reusing the same teamDatabase snapshot for
  // both instead of querying it twice.
  useEffect(() => {
    if (!activeCompany?.companyId) return;
    database
      .collection('teamDatabase')
      .where('ownedBy', '==', activeCompany.companyId)
      .get()
      .then(async (snap) => {
        const names = {};
        snap.docs.forEach((doc) => {
          names[doc.id] = doc.data()?.teamName || doc.id;
        });
        setTeamNameMap(names);

        const teamIds = snap.docs.map((d) => d.id);
        const byUid = {};
        await Promise.all(
          teamIds.map(async (teamId) => {
            const memberDetails = await getTeamMemberDetails(database, teamId);
            Object.entries(memberDetails || {}).forEach(
              ([userUID, details]) => {
                const role = details.userType || details.role || '';
                if (MANAGER_ROLES.includes(role)) {
                  byUid[userUID] = {
                    userUID,
                    userName:
                      details.userName || details.accountFullName || userUID,
                    role,
                  };
                }
              }
            );
          })
        );
        setCompanyManagers(Object.values(byUid));
      })
      .catch(() => {
        setCompanyManagers([]);
        setTeamNameMap({});
      });
  }, [activeCompany?.companyId]);

  useEffect(() => {
    if (activeCompany?.companyId) fetchLeaveRequestsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.companyId]);

  const dateFilteredRows = useMemo(() => {
    const [start, end] = dateRange || [];
    if (!start || !end) return leaveRequestsList;
    return leaveRequestsList.filter((row) => {
      const d = parseLeaveDateString(row.leaveDate);
      return Boolean(d) && d >= start && d <= end;
    });
  }, [leaveRequestsList, dateRange]);

  const filteredRows = useMemo(() => {
    return dateFilteredRows.filter((row) => {
      if (typeFilter?.value && row.type !== typeFilter.value) return false;
      if (statusFilter?.value && row.status !== statusFilter.value)
        return false;
      return true;
    });
  }, [dateFilteredRows, typeFilter, statusFilter]);

  const sortedRows = useMemo(() => {
    if (!sortedBy) return filteredRows;
    const getTime = (row) => {
      const date =
        sortedBy === 'leaveDate'
          ? parseLeaveDateString(row.leaveDate)
          : row.requestDate?.toDate?.();
      return date ? date.getTime() : 0;
    };
    const sorted = [...filteredRows].sort((a, b) => getTime(a) - getTime(b));
    return reverseSort ? sorted.reverse() : sorted;
  }, [filteredRows, sortedBy, reverseSort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, typeFilter, statusFilter, sortedBy, reverseSort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pagedRows = sortedRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleSortClick = (id, reverse) => {
    setSortedBy(id);
    setReverseSort(reverse);
  };

  const openUpdateModal = (row) => {
    rowDataRef.current = row;
    setShowUpdateLeaveModal(true);
  };
  const onCloseAddLeaveFormModal = () => setShowAddLeaveFormModal(false);
  const onCloseUpdateLeaveModal = () => setShowUpdateLeaveModal(false);

  const onActionsButtonClick = (e, rowData) => {
    setActiveMenuRow(rowData);
    menuRef.current.toggle(e);
  };

  const handleDeleteRequest = async () => {
    if (!deleteRequest || isDeleting) return;
    setIsDeleting(true);
    try {
      await database
        .collection('companyDatabase')
        .doc(activeCompany.companyId)
        .collection('leaveRequests')
        .doc(deleteRequest.id)
        .delete();
      addNotification({
        message: 'Request deleted successfully',
        level: notificationStates.success,
      });
      setDeleteRequest(null);
      fetchLeaveRequestsList();
    } catch (error) {
      console.error('Error deleting leave request:', error);
      addNotification({
        message: 'Request deletion failed',
        level: notificationStates.error,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const actionsMenuItems = useMemo(() => {
    if (!activeMenuRow) return [];
    const items = [];
    if (activeMenuRow.status !== 'Approved') {
      items.push({
        label: 'Edit',
        icon: <FiEdit2 size={16} />,
        onClick: () => {
          openUpdateModal(activeMenuRow);
          menuRef.current?.hide();
        },
      });
    }
    items.push({
      label: 'View',
      icon: <FiEye size={16} />,
      onClick: () => {
        setViewRequest(activeMenuRow);
        menuRef.current?.hide();
      },
    });
    if (activeMenuRow.status !== 'Approved') {
      items.push({
        label: 'Delete',
        icon: <FiTrash2 size={16} />,
        danger: true,
        onClick: () => {
          setDeleteRequest(activeMenuRow);
          menuRef.current?.hide();
        },
      });
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenuRow]);

  const getManagerLabel = (row) => {
    if (!row) return '';
    const manager = companyManagers.find((m) => m.userUID === row.assignee);
    if (!manager) return '';
    return manager.role
      ? `${manager.userName} (${manager.role})`
      : manager.userName;
  };

  const exportToCSV = () => {
    if (sortedRows.length === 0) {
      addNotification({
        message: 'No data to export for the selected filters.',
        level: notificationStates.warning,
      });
      return;
    }
    const headers = [
      'ID',
      'Comment',
      'Type',
      'Leave Date',
      'Report Date',
      'Manager',
      'Department',
      'Status',
    ];
    const rows = sortedRows.map((row, index) => {
      const manager = companyManagers.find((m) => m.userUID === row.assignee);
      const reportDate = row.requestDate?.toDate?.();
      return [
        escapeCSVField(index + 1),
        escapeCSVField(row.comment),
        escapeCSVField(row.type),
        escapeCSVField(row.leaveDate),
        escapeCSVField(reportDate ? reportDate.toLocaleDateString() : ''),
        escapeCSVField(manager?.userName),
        escapeCSVField(teamNameMap[row.teamId] || row.teamId),
        escapeCSVField(row.status),
      ].join(',');
    });
    const date = new Date().toISOString().split('T')[0];
    downloadCSV({ headers, rows, filename: `leave-requests-${date}.csv` });
    addNotification({
      message: 'CSV exported successfully',
      level: notificationStates.success,
    });
  };

  const clearFilters = () => {
    setTypeFilter(LEAVE_TYPE_FILTER_OPTIONS[0]);
    setStatusFilter(LEAVE_STATUS_FILTER_OPTIONS[0]);
  };

  const filterPopoverContent = (
    <div className={styles.filterPopover}>
      <Dropdown
        label="Type"
        options={LEAVE_TYPE_FILTER_OPTIONS}
        value={typeFilter}
        onChange={setTypeFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Dropdown
        label="Status"
        options={LEAVE_STATUS_FILTER_OPTIONS}
        value={statusFilter}
        onChange={setStatusFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Button
        text="Clear filters"
        variant={buttonVariants.ghost}
        onClick={clearFilters}
      />
    </div>
  );

  const columns = [
    {
      id: 'id',
      name: 'ID',
      style: { width: '4%' },
      cellRenderer: (row, rowIndex) => (
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {(currentPage - 1) * PAGE_SIZE + rowIndex + 1}
        </Text>
      ),
    },
    {
      id: 'comment',
      name: 'Comment',
      style: { width: '10%' },
      cellRenderer: (row) => <PlainCell>{row.comment}</PlainCell>,
    },
    {
      id: 'type',
      name: 'Type',
      style: { width: '11%' },
      cellRenderer: (row) => (
        <StatusBadge
          label={row.type}
          tone={LEAVE_TYPE_TONE[row.type] || statusBadgeTones.default}
        />
      ),
    },
    {
      id: 'leaveDate',
      name: 'Leave Date',
      sortable: true,
      sortKey: 'leaveDate',
      style: { width: '11%' },
      cellRenderer: (row) => (
        <DateCell date={parseLeaveDateString(row.leaveDate)} />
      ),
    },
    {
      id: 'reportDate',
      name: 'Report Date',
      sortable: true,
      sortKey: 'reportDate',
      style: { width: '11%' },
      cellRenderer: (row) => <DateCell date={row.requestDate?.toDate?.()} />,
    },
    {
      id: 'manager',
      name: 'Manager',
      style: { width: '14%' },
      cellRenderer: (row) => (
        <ManagerCell
          manager={companyManagers.find((m) => m.userUID === row.assignee)}
        />
      ),
    },
    {
      id: 'department',
      name: 'Department',
      style: { width: '12%' },
      cellRenderer: (row) => (
        <DepartmentCell teamName={teamNameMap[row.teamId] || row.teamId} />
      ),
    },
    {
      id: 'status',
      name: 'Status',
      style: { width: '9%' },
      cellRenderer: (row) => (
        <StatusBadge
          label={row.status}
          tone={LEAVE_STATUS_TONE[row.status] || statusBadgeTones.default}
          dot
        />
      ),
    },
    {
      id: 'actions',
      name: 'Actions',
      style: { width: '9%' },
      cellRenderer: (row) => (
        <Button
          icon={<FiMoreVertical size={14} />}
          variant={buttonVariants.outline}
          ariaLabel="Actions"
          onClick={(e) => onActionsButtonClick(e, row)}
        />
      ),
    },
  ];

  return (
    <>
      <div className={styles.leavePage}>
        <div className={styles.leaveContainer}>
          <div className={styles.leaveHeader}>
            <Button
              text="Add Leave Request"
              icon={<FiPlus size={16} />}
              variant={buttonVariants.primary_rounded}
              onClick={() => setShowAddLeaveFormModal(true)}
              disabled={leaveData.loading}
            />
            <div className={styles.leaveHeaderActions}>
              <DateRangePicker value={dateRange} onChange={setDateRange} />
              <Popover
                content={filterPopoverContent}
                trigger="click"
                placement="bottomRight"
              >
                <Button
                  text="Filter"
                  icon={<FiFilter size={14} />}
                  variant={buttonVariants.outline}
                />
              </Popover>
              <Button
                text="Export"
                icon={<FiDownload size={14} />}
                variant={buttonVariants.outline}
                onClick={exportToCSV}
              />
            </div>
          </div>

          <div className={styles.statsRow}>
            {STAT_CARDS_CONFIG.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                iconBg={card.iconBg}
                label={card.label}
                value={leaveData[card.key]}
              />
            ))}
          </div>

          <div className={styles.leaveTableContainer}>
            {loading ? (
              <div className="w-100 text-center py-5">
                <Spinner />
              </div>
            ) : sortedRows.length === 0 ? (
              <EmptyState
                illustration
                title="No Leave Requests"
                description="No leave requests match the selected filters."
              />
            ) : (
              <>
                <Table
                  className={styles.leaveTable}
                  data={pagedRows}
                  columns={columns}
                  onSortClick={handleSortClick}
                  sortedBy={sortedBy}
                  reverseSort={reverseSort}
                />
                <div className={styles.tableFooter}>
                  <Text variant={textVariantsEnum.Text_sm} color="gray-500">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                    {Math.min(currentPage * PAGE_SIZE, sortedRows.length)} of{' '}
                    {sortedRows.length} requests
                  </Text>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.regularizationBanner}>
          <span className={styles.regularizationBannerIcon}>
            <FiCalendar size={20} color="var(--indigo-600)" />
          </span>
          <div className={styles.regularizationBannerText}>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Semibold}
              color="gray-900"
            >
              Streamline leave approvals
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Normal}
              color="gray-600"
            >
              Use regularization to mark unreported leaves or adjust attendance.
            </Text>
          </div>
          <Button
            text="Go to Regularization"
            variant={buttonVariants.ghost}
            icon={<FiChevronRight size={16} />}
            iconPosition="right"
            onClick={() => history.push(appRoutes().regularization)}
          />
        </div>
      </div>

      {/* Modal for creating leave request */}
      <LeaveFormModal
        isVisible={showAddLeaveFormModal}
        companyManagers={companyManagers}
        onClose={onCloseAddLeaveFormModal}
        fetchLeaveRequestsList={fetchLeaveRequestsList}
        fetchLeaveBalance={fetchLeaveBalance}
        leaveData={leaveData}
      />
      <UpdateLeaveFormModal
        isVisible={showUpdateLeaveModal}
        leaveData={rowDataRef.current}
        companyManagers={companyManagers}
        onClose={onCloseUpdateLeaveModal}
        fetchLeaveRequestsList={fetchLeaveRequestsList}
      />
      <ViewLeaveModal
        isVisible={Boolean(viewRequest)}
        rowData={viewRequest}
        reporterLabel={
          (profile?.name && String(profile.name).trim()) || profile?.email || ''
        }
        managerLabel={getManagerLabel(viewRequest)}
        departmentLabel={
          viewRequest
            ? teamNameMap[viewRequest.teamId] || viewRequest.teamId
            : ''
        }
        onClose={() => setViewRequest(null)}
      />
      <ActionsMenu items={actionsMenuItems} ref={menuRef} />
      <ConfirmActionDialog
        visible={Boolean(deleteRequest)}
        onHide={() => setDeleteRequest(null)}
        title="Delete Leave Request"
        message="Are you sure you want to delete this leave request? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDeleteRequest}
        loading={isDeleting}
      />
    </>
  );
};

LeaveComponent.displayName = 'Leave';
